<template>
  <section class="hero-section">
    <!-- Background pattern -->
    <div class="background-pattern">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
      <!-- Particles -->
      <div 
        v-for="i in 20" 
        :key="i"
        class="particle"
        :style="getParticleStyle(i)"
      ></div>
    </div>

    <div class="container">
      <div class="content">
        <h1 class="hero-title" v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6 }">
          创新医疗<br class="hidden md:block" />
          <span class="hero-title-highlight">守护您的健康未来</span>
        </h1>

        <p class="hero-subtitle" v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: 200 }">
          我们致力于提供卓越的医疗服务和创新的治疗方案，
          为患者带来新希望与健康。
        </p>

        <div class="cta-buttons" v-motion :initial="{ opacity: 0, y: 20 }" :enter="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.6, delay: 400 }">
          <a href="#patientRegistration" class="primary-button">
            患者免费用药报名
          </a>
          <a href="#about" class="secondary-button">
            了解更多
          </a>
        </div>
      </div>
    </div>

    <!-- Wave separator -->
    <div class="wave-separator">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" class="wave-svg">
        <path class="wave-path"
          d="M0,64L60,69.3C120,75,240,85,360,80C480,75,600,53,720,48C840,43,960,53,1080,64C1200,75,1320,85,1380,90.7L1440,96L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z">
        </path>
      </svg>
    </div>
  </section>
</template>

<script setup>
// 生成粒子的随机样式
const getParticleStyle = (index) => {
  // 使用索引生成伪随机数
  const seed = Math.sin(index) * 10000;
  const random1 = (seed - Math.floor(seed)) * 100;
  const random2 = (seed * 2 - Math.floor(seed * 2)) * 100;
  const random3 = (seed * 3 - Math.floor(seed * 3)) * 100;
  const random4 = (seed * 4 - Math.floor(seed * 4)) * 5;
  const random5 = (seed * 5 - Math.floor(seed * 5)) * 360;
  
  return {
    width: `${2 + random4}px`,
    height: `${2 + random4}px`,
    top: `${random1}%`,
    left: `${random2}%`,
    animationDelay: `${random3 * 20}ms`,
    transform: `rotate(${random5}deg)`
  };
};
</script>

<style scoped>
.hero-section {
  position: relative;
  background: linear-gradient(to right, #2563eb, #1e40af);
  color: white;
  overflow: hidden;
}

.background-pattern {
  position: absolute;
  inset: 0;
  opacity: 0.1;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background-color: white;
  animation: float 6s ease-in-out infinite;
}

.circle-1 {
  width: 16rem;
  height: 16rem;
  top: -2.5rem;
  right: -2.5rem;
  animation-delay: 0s;
}

.circle-2 {
  width: 8rem;
  height: 8rem;
  left: 25%;
  bottom: 25%;
  animation-delay: 2s;
}

.circle-3 {
  width: 6rem;
  height: 6rem;
  right: 33.333333%;
  top: 33.333333%;
  animation-delay: 4s;
}

@keyframes float {
  0% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, 30px);
  }
  100% {
    transform: translate(0, 0);
  }
}

.particle {
  position: absolute;
  background-color: rgba(255, 255, 255, 1.2);
  border-radius: 50%;
  animation: floatParticle 10s linear infinite;
  box-shadow: 0 0 40px rgba(255, 255, 255, 0.5);
}

@keyframes floatParticle {
  0% {
    transform: translateY(0) translateX(0) rotate(0deg) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-200px) translateX(50px) rotate(720deg) scale(0.2);
    opacity: 0;
  }
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  padding-top: 5rem;
  padding-bottom: 5rem;
  position: relative;
  z-index: 10;
}

@media (min-width: 768px) {
  .container {
    padding: 8rem 1.5rem;
  }
}

.content {
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
}

.hero-title {
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.25;
}

@media (min-width: 768px) {
  .hero-title {
    font-size: 3rem;
  }
}

@media (min-width: 1024px) {
  .hero-title {
    font-size: 3.75rem;
  }
}

.hero-title-highlight {
  color: #dbeafe;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #dbeafe;
  margin-bottom: 2rem;
  line-height: 1.75;
}

@media (min-width: 768px) {
  .hero-subtitle {
    font-size: 1.5rem;
  }
}

.cta-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
}

@media (min-width: 640px) {
  .cta-buttons {
    flex-direction: row;
  }
}

.primary-button {
  background-color: white;
  color: #1d4ed8;
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  font-size: 1.125rem;
}

.primary-button:hover {
  background-color: #eff6ff;
  transform: scale(1.05);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.secondary-button {
  background-color: transparent;
  color: white;
  border: 2px solid white;
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  transition: all 0.3s ease;
  text-decoration: none;
  font-size: 1.125rem;
}

.secondary-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.wave-separator {
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
}

.wave-svg {
  width: 100%;
  height: auto;

}

.wave-path {
  fill: #ffffff;
  fill-opacity: 1;
}


@media (max-width: 767px) {
  .container {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
  
  .hero-title {
    font-size: 1.75rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .primary-button, .secondary-button {
    padding: 0.5rem 1.5rem;
    font-size: 1rem;
  }
  
  .circle-1 {
    width: 8rem;
    height: 8rem;
  }
  
  .circle-2 {
    width: 4rem;
    height: 4rem;
  }
  
  .circle-3 {
    width: 3rem;
    height: 3rem;
  }
}
</style>